
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { Users } from "lucide-react";
import { useState } from "react";
import { useTeamData } from "./hooks/useTeamData";
import { useTeamActions } from "./hooks/useTeamActions";
import TeamMemberRow from "./components/TeamMemberRow";
import PendingInvitationRow from "./components/PendingInvitationRow";
import SuspensionDialog from "./components/SuspensionDialog";
import ConfirmationDialog from "@/components/admin/components/ConfirmationDialog";

const TeamMembers = () => {
  const { user, profile, isAdmin: canManageMembers } = useAuth();
  const {
    members,
    setMembers,
    pendingInvitations,
    setPendingInvitations,
    isLoading,
    companyName
  } = useTeamData();

  const {
    handleMakeAdmin,
    handleRemoveAdmin,
    handleDeactivateMember,
    handleReactivateMember,
    handleCancelInvitation,
    handleResendInvitation,
    handleSuspendMember,
    handleUnsuspendMember,
    suspensionDialog,
    setSuspensionDialog,
    handleSuspensionComplete,
    confirmDialog,
    closeConfirmDialog
  } = useTeamActions(members, setMembers, setPendingInvitations, companyName, profile?.company_id);

  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'suspended' | 'deactivated' | 'pending'>('active');
  

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Team Members
          </CardTitle>
          <CardDescription>
            Manage your company team members
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const activeMembers = members.filter(member => member.status === 'active');
  const suspendedMembers = members.filter(member => member.status === 'suspended');
  const deactivatedMembers = members.filter(member => member.status === 'deactivated');
  
  const displayedMembers = (() => {
    switch (activeFilter) {
      case 'active':
        return activeMembers;
      case 'suspended':
        return suspendedMembers;
      case 'deactivated':
        return deactivatedMembers;
      case 'pending':
        return [];
      case 'all':
        return members;
      default:
        return activeMembers;
    }
  })();
  
  const totalTeamSize = members.length + pendingInvitations.length;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Team Members ({totalTeamSize})
          </CardTitle>
          <CardDescription>
            Manage your company team members and pending invitations
          </CardDescription>
          
          {/* Status Filter Badges */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4">
            <div 
              className={`flex items-center gap-2 p-2 rounded-md cursor-pointer transition-all hover:bg-accent ${
                activeFilter === 'active' ? 'bg-accent ring-2 ring-green-500' : ''
              }`}
              onClick={() => setActiveFilter('active')}
            >
              <Badge variant="secondary" className="bg-green-100 text-green-800">
                {activeMembers.length}
              </Badge>
              <span className="text-sm text-muted-foreground">Active</span>
            </div>
            
            <div 
              className={`flex items-center gap-2 p-2 rounded-md cursor-pointer transition-all hover:bg-accent ${
                activeFilter === 'suspended' ? 'bg-accent ring-2 ring-yellow-500' : ''
              }`}
              onClick={() => setActiveFilter('suspended')}
            >
              <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                {suspendedMembers.length}
              </Badge>
              <span className="text-sm text-muted-foreground">Suspended</span>
            </div>

            <div 
              className={`flex items-center gap-2 p-2 rounded-md cursor-pointer transition-all hover:bg-accent ${
                activeFilter === 'deactivated' ? 'bg-accent ring-2 ring-red-500' : ''
              }`}
              onClick={() => setActiveFilter('deactivated')}
            >
              <Badge variant="secondary" className="bg-red-100 text-red-800">
                {deactivatedMembers.length}
              </Badge>
              <span className="text-sm text-muted-foreground">Deactivated</span>
            </div>

            <div 
              className={`flex items-center gap-2 p-2 rounded-md cursor-pointer transition-all hover:bg-accent ${
                activeFilter === 'pending' ? 'bg-accent ring-2 ring-blue-500' : ''
              }`}
              onClick={() => setActiveFilter('pending')}
            >
              <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                {pendingInvitations.length}
              </Badge>
              <span className="text-sm text-muted-foreground">Pending</span>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {totalTeamSize === 0 ? (
            <div className="text-center py-6 text-gray-500">
              No team members found. Invite colleagues to join your company.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Member</TableHead>
                  <TableHead className="hidden sm:table-cell">Contact</TableHead>
                  <TableHead className="hidden md:table-cell">Company Role</TableHead>
                  <TableHead className="hidden lg:table-cell">App User Type</TableHead>
                  <TableHead>Status</TableHead>
                  {activeFilter === 'deactivated' && <TableHead className="hidden md:table-cell">Deactivated</TableHead>}
                  {canManageMembers && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {activeFilter === 'pending' ? (
                  pendingInvitations.length > 0 ? (
                    pendingInvitations.map((invitation) => (
                      <PendingInvitationRow
                        key={invitation.id}
                        invitation={invitation}
                        canManageMembers={canManageMembers}
                        onResendInvitation={handleResendInvitation}
                        onCancelInvitation={handleCancelInvitation}
                      />
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-6 text-gray-500">
                        No pending invitations
                      </TableCell>
                    </TableRow>
                  )
                ) : (
                  displayedMembers.length > 0 ? (
                    displayedMembers.map((member) => (
                      <TeamMemberRow
                        key={member.id}
                        member={member}
                        currentUserId={user?.id}
                        canManageMembers={canManageMembers}
                        showDeactivated={activeFilter === 'deactivated'}
                        onMakeAdmin={handleMakeAdmin}
                        onRemoveAdmin={handleRemoveAdmin}
                        onRemoveMember={handleDeactivateMember}
                        onReactivateMember={handleReactivateMember}
                        onSuspendMember={handleSuspendMember}
                        onUnsuspendMember={handleUnsuspendMember}
                      />
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={activeFilter === 'deactivated' ? 7 : 6} className="text-center py-6 text-gray-500">
                        {activeFilter === 'active' && 'No active team members found'}
                        {activeFilter === 'suspended' && 'No suspended team members found'}
                        {activeFilter === 'deactivated' && 'No deactivated team members found'}
                        {activeFilter === 'all' && 'No team members found'}
                      </TableCell>
                    </TableRow>
                  )
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <SuspensionDialog
        isOpen={suspensionDialog.isOpen}
        onClose={() => setSuspensionDialog(prev => ({ ...prev, isOpen: false }))}
        memberId={suspensionDialog.memberId}
        memberName={suspensionDialog.memberName}
        onSuspensionComplete={handleSuspensionComplete}
      />

      <ConfirmationDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        description={confirmDialog.description}
        actionLabel="Confirm"
        onClose={closeConfirmDialog}
        onConfirm={confirmDialog.onConfirm}
      />
    </>
  );
};

export default TeamMembers;
